import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Utility function to merge Tailwind CSS classes with proper conflict resolution
 * @param inputs - Class values to merge
 * @returns Merged class string
 */
export function cn(...inputs: ClassValue[]) {
    const mergedClasses = twMerge(clsx(inputs));
    
    // For modern inputs, aggressively remove conflicting text and background colors
    if (mergedClasses.includes("modern-input")) {
        return mergedClasses
            .replace(/\btext-white\b/g, "")
            .replace(/\btext-gray-\d+\b/g, "")
            .replace(/\bbg-white\/\d+\b/g, "")
            .replace(/\bbg-gray-\d+\/?\d*\b/g, "")
            .replace(/\bplaceholder-white\/?\d*\b/g, "")
            .replace(/\bplaceholder-gray-\d+\/?\d*\b/g, "")
            .replace(/\s+/g, " ")
            .trim();
    }
    
    // Legacy glass-input support for backward compatibility
    if (mergedClasses.includes("glass-input")) {
        return mergedClasses
            .replace(/\btext-white\b/g, "")
            .replace(/\btext-gray-\d+\b/g, "")
            .replace(/\bbg-white\/\d+\b/g, "")
            .replace(/\bbg-gray-\d+\/?\d*\b/g, "")
            .replace(/\bplaceholder-white\/?\d*\b/g, "")
            .replace(/\bplaceholder-gray-\d+\/?\d*\b/g, "")
            .replace(/\s+/g, " ")
            .trim() + " modern-input"; // Migrate to modern input
    }

    return mergedClasses;
}

/**
 * Button variant styles using modern design system
 */
export const buttonVariants = {
    variant: {
        default: "modern-button modern-button-primary",
        secondary: "modern-button modern-button-secondary", 
        outline: "modern-button modern-button-secondary",
        ghost: "modern-button modern-button-ghost",
        destructive: "modern-button modern-button-primary bg-red-500 hover:bg-red-600",
        link: "text-primary underline-offset-4 hover:underline",
    },
    size: {
        default: "py-3 px-6",
        sm: "py-2 px-4 text-sm",
        lg: "py-4 px-8 text-lg", 
        icon: "h-10 w-10 p-0",
    },
};

/**
 * Card variant styles using modern design system
 */
export const cardVariants = {
    variant: {
        default: "modern-card",
        outline: "modern-card border-2",
        filled: "modern-card bg-muted",
        elevated: "modern-panel-emphasis",
        interactive: "modern-card-interactive",
        compact: "modern-panel-compact",
    },
};

/**
 * Alert variant styles
 */
export const alertVariants = {
    variant: {
        default: "alert",
        destructive: "alert alert-destructive",
        warning: "alert alert-warning",
        info: "alert alert-info",
        success: "alert alert-success",
    },
};
